import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';

const CookieBanner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.5rem;
  z-index: 1000;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  transform: ${props => props.$show ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s ease-in-out;
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const BannerText = styled.div`
  flex: 1;
  min-width: 300px;
`;

const BannerTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const BannerDescription = styled.p`
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
  line-height: 1.4;
`;

const BannerLink = styled(Link)`
  color: white;
  text-decoration: underline;
  font-weight: 600;
  &:hover {
    color: rgba(255, 255, 255, 0.95);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  
  ${props => props.$primary ? `
    background: #27ae60;
    color: white;
    
    &:hover {
      background: #229954;
      transform: translateY(-1px);
    }
  ` : props.$secondary ? `
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  ` : `
    background: #e74c3c;
    color: white;
    
    &:hover {
      background: #c0392b;
    }
  `}
`;

const SettingsModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  color: #333;
`;

const ModalTitle = styled.h2`
  margin: 0 0 1.5rem 0;
  color: #2c3e50;
  text-align: center;
`;

const CookieCategory = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  border: 1px solid #ecf0f1;
  border-radius: 8px;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const CategoryTitle = styled.h3`
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
`;

const CategoryDescription = styled.p`
  margin: 0.5rem 0 0 0;
  color: #7f8c8d;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background-color: #27ae60;
  }
  
  &:checked + span:before {
    transform: translateX(26px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 24px;
  
  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
`;

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookieSettings, setCookieSettings] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const savedSettings = JSON.parse(consent);
      setCookieSettings(savedSettings);
    }
  }, []);

  const notifyConsentUpdate = (settings) => {
    // Dispatch custom event to notify other components (like SEOHead) about consent changes
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: settings }));
    // Also trigger storage event for cross-tab synchronization
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'cookieConsent',
      newValue: JSON.stringify(settings),
      storageArea: localStorage
    }));
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setCookieSettings(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
    notifyConsentUpdate(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    setCookieSettings(onlyEssential);
    localStorage.setItem('cookieConsent', JSON.stringify(onlyEssential));
    setShowBanner(false);
    notifyConsentUpdate(onlyEssential);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(cookieSettings));
    setShowSettings(false);
    setShowBanner(false);
    notifyConsentUpdate(cookieSettings);
  };

  const handleToggleCategory = (category) => {
    if (category === 'essential') return; // Essential cookies cannot be disabled
    setCookieSettings(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const cookieCategories = [
    {
      id: 'essential',
      title: 'Essential Cookies',
      description: 'These cookies are necessary for the website to function properly. They cannot be disabled.',
      required: true
    },
    {
      id: 'analytics',
      title: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website by collecting anonymous information.'
    },
    {
      id: 'marketing',
      title: 'Marketing Cookies',
      description: 'These cookies are used to track visitors across websites to display relevant advertisements.'
    },
    {
      id: 'preferences',
      title: 'Preference Cookies',
      description: 'These cookies remember your choices and preferences to provide a personalized experience.'
    }
  ];

  if (!showBanner && !showSettings) return null;

  return (
    <>
      <CookieBanner $show={showBanner}>
        <BannerContent>
          <BannerText>
            <BannerTitle>🍪 We use cookies</BannerTitle>
            <BannerDescription>
              We use cookies to enhance your browsing experience, serve personalized content, 
              and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.{' '}
              <BannerLink to="/cookie-policy">Learn more</BannerLink>
            </BannerDescription>
          </BannerText>
          <ButtonGroup>
            <Button onClick={handleRejectAll}>Reject All</Button>
            <Button $secondary onClick={() => setShowSettings(true)}>Customize</Button>
            <Button $primary onClick={handleAcceptAll}>Accept All</Button>
          </ButtonGroup>
        </BannerContent>
      </CookieBanner>

      <SettingsModal $show={showSettings}>
        <ModalContent>
          <ModalTitle>Cookie Preferences</ModalTitle>
          <p style={{ marginBottom: '1.5rem', color: '#7f8c8d' }}>
            Choose which cookies you want to allow. You can change these settings at any time.
          </p>
          
          {cookieCategories.map(category => (
            <CookieCategory key={category.id}>
              <CategoryHeader>
                <CategoryTitle>{category.title}</CategoryTitle>
                <ToggleSwitch>
                  <ToggleInput
                    type="checkbox"
                    checked={cookieSettings[category.id]}
                    onChange={() => handleToggleCategory(category.id)}
                    disabled={category.required}
                  />
                  <ToggleSlider />
                </ToggleSwitch>
              </CategoryHeader>
              <CategoryDescription>{category.description}</CategoryDescription>
            </CookieCategory>
          ))}
          
          <ModalButtons>
            <Button $secondary onClick={() => setShowSettings(false)}>
              Cancel
            </Button>
            <Button $primary onClick={handleSaveSettings}>
              Save Preferences
            </Button>
          </ModalButtons>
        </ModalContent>
      </SettingsModal>
    </>
  );
};

export default CookieConsent;
